import { useState } from "react";
import { Download } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "~/components/ui/dialog";
import { cn } from "~/lib/utils";

interface ImageViewProps {
  // Signed URL for the image file.
  url: string;
  // Canonical filename (e.g. "chart.png"). Preferred for alt text and the
  // download attribute.
  filename?: string | null;
  artifactName?: string | null;
  summary?: string | null;
  // "full" (default) sizes the inline image for reading — agent-returned
  // artifacts the user wants to actually see. "thumbnail" clamps it to a small
  // box: a user-attached image is context they already recognize, so the
  // lightbox carries the full view.
  variant?: "full" | "thumbnail";
}

// Below this threshold the inline download chip dwarfs the image, so we hide
// it and rely on the lightbox's labeled Download action instead.
const DOWNLOAD_CHIP_MIN_SIZE = 100;

// Minimal image renderer shared by agent-returned image artifacts and sent
// user attachments: a bare rounded image with a corner download chip that
// opens a click-to-zoom lightbox. No card frame — intentionally distinct from
// the generic ArtifactShell used by tabular/figure/code types.
export function ImageView({
  url,
  filename,
  artifactName,
  summary,
  variant = "full",
}: ImageViewProps) {
  const altText = summary || filename || artifactName || "Image artifact";
  const title = filename || artifactName || "Image";
  const downloadName = filename ?? "";
  const [showDownloadChip, setShowDownloadChip] = useState(false);

  return (
    <Dialog>
      <div className="relative inline-block">
        <DialogTrigger asChild>
          <button
            type="button"
            className="block cursor-zoom-in overflow-hidden rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            aria-label={`Open ${title}`}
          >
            <img
              src={url}
              alt={altText}
              className={cn(
                "object-contain",
                variant === "thumbnail" ? "max-h-48 max-w-48" : "max-h-96 max-w-full"
              )}
              onLoad={(e) => {
                const img = e.currentTarget;
                setShowDownloadChip(
                  img.naturalWidth >= DOWNLOAD_CHIP_MIN_SIZE &&
                    img.naturalHeight >= DOWNLOAD_CHIP_MIN_SIZE
                );
              }}
            />
          </button>
        </DialogTrigger>

        {showDownloadChip && (
          <a
            href={url}
            download={downloadName}
            className="absolute bottom-2 right-2 flex size-7 items-center justify-center rounded-full bg-black/40 text-white transition-colors hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            aria-label="Download image"
          >
            <Download className="size-3.5" />
          </a>
        )}
      </div>

      <DialogContent
        className="flex max-h-[95vh] w-fit min-w-[320px] max-w-[95vw] flex-col gap-3 sm:max-w-[95vw]"
        closeButtonClassName="focus:ring-0 focus:ring-offset-0 focus-visible:bg-gray-200 active:bg-gray-300"
      >
        <DialogTitle className="truncate pr-10 text-sm font-medium">{title}</DialogTitle>
        <DialogDescription className="sr-only">
          {summary || `Full-size view of ${title}`}
        </DialogDescription>
        <div className="relative flex items-center justify-center rounded bg-black/5">
          <img src={url} alt={altText} className="max-h-[80vh] max-w-full object-contain" />
          {showDownloadChip && (
            <a
              href={url}
              download={downloadName}
              className="absolute bottom-2 right-2 flex size-9 items-center justify-center rounded-full bg-black/40 text-white transition-colors hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              aria-label="Download image"
            >
              <Download className="size-4" />
            </a>
          )}
        </div>
        {summary && <p className="whitespace-pre-wrap text-sm text-gray-700">{summary}</p>}
        {!showDownloadChip && (
          <div className="flex justify-end">
            <Button asChild variant="outline" size="sm" aria-label="Download image">
              <a href={url} download={downloadName}>
                <Download className="size-3.5" />
                Download
              </a>
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
